import React, { memo, useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Logo from "../Logo";
import RegisterModal from "../Auth/RegisterModal";
import styles from "./LoginModal.module.css";
import { useAuth } from "../../contexts/JWTContext";

const LoginModal = memo(({ isOpen, onClose, onOpenRegister, hideClose }) => {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // track if modal was opened via global event
  const [openFromEvent, setOpenFromEvent] = useState(false);

  const closeModal = useCallback(() => {
    setOpenFromEvent(false);
    setError("");
    setFormData({ username: "", password: "" });
    if (onClose) onClose();
  }, [onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleEscapeKey = useCallback(
    (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    const onOpenLogin = () => {
      setError("");
      setFormData({ username: "", password: "" });
      setOpenFromEvent(true);
    };
    window.addEventListener("open-login-modal", onOpenLogin);
    return () => window.removeEventListener("open-login-modal", onOpenLogin);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setLoading(true);
      try {
        await login(formData.username.trim(), formData.password);
        setLoading(false);
        setFormData({ username: "", password: "" });
        closeModal();
      } catch (err) {
        setLoading(false);
        setError(
          err?.response?.data?.message || err?.message || "Login failed",
        );
      }
    },
    [formData, login, closeModal],
  );

  const handleOpenRegister = useCallback(() => {
    if (onOpenRegister) {
      onOpenRegister(); // delegate to parent if provided
    } else {
      setShowRegister(true); // fallback: open internal register modal
    }
    closeModal();
  }, [onOpenRegister, closeModal]);

  useEffect(() => {
    const active = isOpen || openFromEvent || showRegister;
    if (active) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, openFromEvent, showRegister, handleEscapeKey]);

  if (!isOpen && !showRegister && !openFromEvent) return null;

  return (
    <>
      {(isOpen || openFromEvent) && (
        <div className={styles.overlay} onClick={handleOverlayClick}>
          <div className={styles.modal}>
            {!hideClose && (
              <Icon
                className={styles.closeButton}
                icon="line-md:close-circle-twotone"
                style={{ color: "red", fontSize: "40px" }}
                onClick={closeModal}
              />
            )}

            <div className={styles.content}>
              <div className={styles.loginHeader}>
                <Logo size="xxlarge" />
                <h2 className={styles.loginTitle}>เข้าสู่ระบบ</h2>
              </div>

              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:person-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type="text"
                      name="username"
                      className={styles.input}
                      placeholder="ชื่อผู้ใช้งาน (เบอร์โทรศัพท์)"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:lock-person-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={styles.input}
                      placeholder="รหัสผ่าน"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={togglePasswordVisibility}
                    >
                      <Icon
                        icon={
                          showPassword
                            ? "material-symbols:visibility-off-rounded"
                            : "material-symbols:visibility-rounded"
                        }
                        width={24}
                      />
                    </button>
                  </div>
                </div>

                {error && <div className={styles.errorText}>{error}</div>}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
                </button>

                <div className={styles.forgotPassword}>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={handleOpenRegister}
                  >
                    ไม่มีบัญชี? <span>สร้างบัญชีใหม่</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showRegister && (
        <RegisterModal
          onClose={() => {
            setShowRegister(false);
          }}
          onSwitchToLogin={() => {
            setShowRegister(false);
            // reopen login if desired
          }}
        />
      )}
    </>
  );
});

LoginModal.displayName = "LoginModal";

export default LoginModal;
